import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, Technique, TournamentProblem, PracticeSession } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trophy, CheckCircle, XCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ProblemAttempt {
  problem: TournamentProblem;
  userAnswer: string;
  correctAnswer?: string; // May not be known if answer was wrong
  isCorrect: boolean;
  timeTaken: number;
}

export default function TournamentArena() {
  const [searchParams] = useSearchParams();
  const sessionIdFromUrl = searchParams.get("session");
  const tournamentIdFromUrl = searchParams.get("tournament");
  const navigate = useNavigate();

  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [session, setSession] = useState<PracticeSession | null>(null);
  const [currentProblem, setCurrentProblem] =
    useState<TournamentProblem | null>(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [problemStartTime, setProblemStartTime] = useState<number>(0);
  const [problemAttempts, setProblemAttempts] = useState<ProblemAttempt[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [loadingProblem, setLoadingProblem] = useState(false);
  const [problemQueue, setProblemQueue] = useState<TournamentProblem[]>([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [tournamentName, setTournamentName] = useState<string>("");

  // Load techniques
  useEffect(() => {
    const loadTechniques = async () => {
      try {
        const data = await api.getTechniques({ view: "list" }); // Fetch minimal data for dropdown
        setTechniques(data);
      } catch (error) {
        console.error("Failed to load techniques:", error);
      }
    };
    loadTechniques();
  }, []);

  // Load tournament session
  useEffect(() => {
    const loadTournamentSession = async () => {
      if (!sessionIdFromUrl || !tournamentIdFromUrl) {
        toast({
          title: "Hmm, that didn't work 😅",
          description:
            "We couldn't find that tournament, so we took you back to the Tournament Hall.",
        });
        navigate("/competitive");
        return;
      }

      console.log("[Tournament] Loading tournament session:", sessionIdFromUrl);
      setLoadingProblem(true);

      try {
        // Fetch tournament details
        const { data: tournamentData } = await supabase
          .from("tournaments")
          .select("name")
          .eq("id", tournamentIdFromUrl)
          .single();

        if (tournamentData) {
          setTournamentName(tournamentData.name);
        }

        // Fetch session details from Supabase
        const { data: sessionData, error } = await supabase
          .from("practice_sessions")
          .select("*")
          .eq("id", sessionIdFromUrl)
          .single();

        if (error || !sessionData) {
          console.error("[Tournament] Failed to load session:", error);
          toast({
            title: "Oops! Something went wrong 😅",
            description:
              "We couldn't load your tournament, so we took you back to the Tournament Hall.",
          });
          navigate("/competitive");
          return;
        }

        console.log("[Tournament] Session loaded:", sessionData);
        setSession(sessionData as PracticeSession);

        // Set session start time for timer
        setSessionStartTime(Date.now());
        setElapsedTime(0);

        // Fetch pre-generated problems from database
        console.log(
          "[Tournament] Fetching pre-generated problems from database",
        );

        const { data: problems, error: problemsError } = await supabase
          .from("tournament_problems")
          .select(
            "id, tournament_id, technique_id, problem_order, question, difficulty",
          )
          // ⚠️ IMPORTANT: Do NOT select 'answer' field - it's server-side only!
          .eq("tournament_id", tournamentIdFromUrl)
          .order("problem_order", { ascending: true });

        if (problemsError || !problems || problems.length === 0) {
          console.error("[Tournament] Failed to load problems:", problemsError);
          toast({
            title: "This tournament isn't ready yet 🛠️",
            description:
              "There are no problems here yet, so we took you back to the Tournament Hall.",
          });
          navigate("/competitive");
          return;
        }

        console.log(
          `[Tournament] Loaded ${problems.length} pre-generated problems`,
        );
        setProblemQueue(problems as TournamentProblem[]);
        setCurrentProblemIndex(0);
        setCurrentProblem(problems[0] as TournamentProblem);

        setProblemStartTime(Date.now());
        setUserAnswer("");
      } catch (error) {
        console.error("[Tournament] Error loading tournament session:", error);
        toast({
          title: "Oops! Something went wrong 😅",
          description:
            "We couldn't load your tournament, so we took you back to the Tournament Hall.",
        });
        navigate("/competitive");
      } finally {
        setLoadingProblem(false);
      }
    };

    loadTournamentSession();
  }, [sessionIdFromUrl, tournamentIdFromUrl, navigate]);

  // Timer for tournament mode (count up - shows elapsed time)
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (session && sessionStartTime > 0) {
      timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [session, sessionStartTime]);

  const submitAnswer = async () => {
    if (!currentProblem || submitting) return;

    const timeTaken = (Date.now() - problemStartTime) / 1000;

    console.log("[Tournament] Recording answer...", {
      question: currentProblem.question,
      userAnswer: userAnswer.trim(),
      timeTaken,
    });

    // Record the answer locally (don't verify yet)
    const attempt: ProblemAttempt = {
      problem: currentProblem,
      userAnswer: userAnswer.trim(),
      correctAnswer: undefined, // Will be filled after submission
      isCorrect: false, // Will be updated after submission
      timeTaken: timeTaken,
    };
    setProblemAttempts((prev) => [...prev, attempt]);

    // Move to next problem immediately
    const nextIndex = currentProblemIndex + 1;
    if (nextIndex < problemQueue.length) {
      setCurrentProblemIndex(nextIndex);
      setCurrentProblem(problemQueue[nextIndex]);
      setProblemStartTime(Date.now());
      setUserAnswer("");
    } else {
      // All questions answered - submit for verification
      console.log(
        "[Tournament] All questions answered, submitting for verification...",
      );
      await handleSubmitAllAnswers([...problemAttempts, attempt]);
    }
  };

  const handleSubmitAllAnswers = async (attempts: ProblemAttempt[]) => {
    if (!session || !tournamentIdFromUrl) return;

    setSubmitting(true);
    setLoadingProblem(true);

    try {
      console.log(`[Tournament] Verifying ${attempts.length} answers...`);

      // Build answers array for bulk verification
      const answersToVerify = attempts.map((attempt) => ({
        problem_id: attempt.problem.id,
        user_answer: attempt.userAnswer,
        time_taken: attempt.timeTaken,
      }));

      // Single bulk verification call
      const result = await api.verifyTournamentAnswers(
        tournamentIdFromUrl,
        answersToVerify,
      );

      // Map results back to attempts
      const resultMap = new Map(result.results.map((r) => [r.problem_id, r]));

      const verifiedAttempts = attempts.map((attempt) => {
        const verification = resultMap.get(attempt.problem.id);
        return {
          ...attempt,
          isCorrect: verification?.is_correct ?? false,
          correctAnswer: verification?.correct_answer,
        };
      });

      const correctCount = result.score.correct;

      console.log(
        `[Tournament] Verification complete: ${correctCount}/${attempts.length} correct`,
      );

      // Update attempts with verification results
      setProblemAttempts(verifiedAttempts);

      // Update session
      const updatedSession = {
        ...session,
        problems_attempted: attempts.length,
        problems_correct: correctCount,
      };
      setSession(updatedSession);

      // End session and show results
      await handleEndSession();
    } catch (error) {
      console.error("[Tournament] Failed to submit answers:", error);
      toast({
        title: "Oops! Something went wrong 😅",
        description: "We couldn't send your answers. Please try again!",
      });
      setSubmitting(false);
      setLoadingProblem(false);
    }
  };

  const handleEndSession = async () => {
    if (!session) return;

    console.log("[Tournament] Ending session...");
    try {
      const totalTime = problemAttempts.reduce(
        (sum, attempt) => sum + attempt.timeTaken,
        0,
      );

      console.log("[Tournament] Completing tournament session...");
      const tournamentResult = await api.completeTournamentSession(
        session.id,
        session.problems_attempted,
        session.problems_correct,
        totalTime,
      );
      console.log(
        "[Tournament] Tournament session completed:",
        tournamentResult,
      );

      // Show results
      setShowResults(true);
      setSession(null);
      setCurrentProblem(null);
    } catch (error) {
      console.error("[Tournament] Failed to end session:", error);
      setShowResults(true);
      setSession(null);
      setCurrentProblem(null);
    }
  };

  const returnToTournaments = () => {
    navigate("/competitive");
  };

  // Get technique name for display
  const getTechniqueName = () => {
    if (!session?.metadata) return "Mixed Techniques";
    const metadata = session.metadata as any;
    if (metadata?.is_multi_technique) return "Mixed Techniques";
    if (session.technique_id) {
      return (
        techniques.find((t) => t.id === session.technique_id)?.name ||
        "Unknown Technique"
      );
    }
    return "Mixed Techniques";
  };

  // Calculate current score
  const currentScore = session
    ? session.problems_correct * 100 - elapsedTime
    : 0;

  // Results screen
  if (showResults) {
    const totalAttempts = problemAttempts.length;
    const correctAnswers = problemAttempts.filter((a) => a.isCorrect).length;
    const accuracy =
      totalAttempts > 0
        ? (Math.round((correctAnswers / totalAttempts) * 1000) / 10).toFixed(1)
        : 0;
    const totalTime = problemAttempts.reduce((sum, a) => sum + a.timeTaken, 0);
    const avgTime =
      totalAttempts > 0 ? (totalTime / totalAttempts).toFixed(1) : 0;
    const finalScore = correctAnswers * 100 - Math.floor(totalTime);

    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="mb-6">
          <CardHeader>
            <div className="text-center">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
              <CardTitle className="text-3xl">
                Tournament Session Complete!
              </CardTitle>
              {tournamentName && (
                <p className="text-lg text-gray-600 mt-2">{tournamentName}</p>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {totalAttempts}
                </div>
                <div className="text-sm text-gray-600">Problems</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {correctAnswers}
                </div>
                <div className="text-sm text-gray-600">Correct</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {accuracy}%
                </div>
                <div className="text-sm text-gray-600">Accuracy</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {finalScore}
                </div>
                <div className="text-sm text-gray-600">Final Score</div>
              </div>
            </div>

            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Time Taken</p>
                <p className="text-3xl font-bold text-blue-600">
                  {Math.floor(totalTime / 60)}:
                  {(Math.floor(totalTime) % 60).toString().padStart(2, "0")}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Average: {avgTime}s per problem
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-lg mb-3">
                Review Your Answers:
              </h3>
              {problemAttempts.map((attempt, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    attempt.isCorrect
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-700">
                        #{index + 1}
                      </span>
                      {attempt.isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {attempt.timeTaken.toFixed(1)}s
                    </span>
                  </div>
                  <div className="text-xl font-mono mb-2">
                    {attempt.problem.question}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Your answer: </span>
                      <span
                        className={
                          attempt.isCorrect
                            ? "text-green-700 font-semibold"
                            : "text-red-700 font-semibold"
                        }
                      >
                        {attempt.userAnswer || "(no answer)"}
                      </span>
                    </div>
                    {attempt.correctAnswer && (
                      <div>
                        <span className="text-gray-600">Correct answer: </span>
                        <span className="text-green-700 font-semibold">
                          {attempt.correctAnswer}
                        </span>
                      </div>
                    )}
                    {!attempt.isCorrect && !attempt.correctAnswer && (
                      <div>
                        <span className="text-gray-600 italic">
                          Correct answer not revealed
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={returnToTournaments}
                className="flex-1"
                size="lg"
              >
                Back to Tournaments
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session || loadingProblem) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading tournament session...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Trophy className="w-6 h-6 text-yellow-600" />
            Tournament Competition
          </h2>
          {tournamentName && (
            <p className="text-lg font-semibold text-blue-600 mt-1">
              {tournamentName}
            </p>
          )}
          <p className="text-base font-medium text-purple-600">
            {getTechniqueName()}
          </p>
          <p className="text-gray-600 mt-1">
            Problem {currentProblemIndex + 1}/{problemQueue.length}
            {" | "}
            Correct: {session.problems_correct}
            {session.problems_correct > 0 && (
              <>
                {" | "}
                <span className="text-green-600 font-semibold">
                  Score: {currentScore}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">
            {Math.floor(elapsedTime / 60)}:
            {(elapsedTime % 60).toString().padStart(2, "0")}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Time Penalty: -{elapsedTime} pts
          </div>
        </div>
      </div>

      {currentProblem ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-4xl font-mono">
              {currentProblem.question}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Answer</label>
              <Input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && !submitting && submitAnswer()
                }
                placeholder="Enter your answer"
                className="text-2xl text-center font-mono"
                autoFocus
                disabled={submitting}
              />
            </div>
            <Button
              className="w-full"
              size="lg"
              onClick={submitAnswer}
              disabled={submitting}
            >
              {currentProblemIndex === problemQueue.length - 1
                ? "Submit All Answers"
                : `Next Question (${currentProblemIndex + 2}/${problemQueue.length})`}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div className="mt-6 flex justify-center">
        <Button variant="outline" onClick={handleEndSession}>
          End Tournament Session
        </Button>
      </div>
    </div>
  );
}
